import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import { ChallengesMilestoneConfigSetUpComponent } from "../challenges-milestone-config-set-up/challenges-milestone-config-set-up.component";
import { ApiCallService } from "src/app/core/services/api-call-service";
import { SnackbarCollection } from "src/app/core/services/snackbar.service";
import { TranslateDynamicText } from "src/app/shared/pipes/dynamic-translation.pipe";
import { Globals } from "src/app/core/services/global.service";
import { Editor, Toolbar } from "ngx-editor";
import { maxLengthHtml } from "src/app/shared/custom-validator";
import { Plugin, PluginKey } from "prosemirror-state";
import { Slice, Fragment } from "prosemirror-model";

@Component({
  selector: "app-challenges-milestone-config",
  templateUrl: "./challenges-milestone-config.component.html",
  styleUrls: [
    "./challenges-milestone-config.component.css",
    "../../../../../../shared/components/dialog-box/campaign-strategy-dialog/campaign-strategy-dialog.component.css",
    "../get-challenge-details-page/get-challenge-details-page.component.css",
  ],
})
export class ChallengesMilestoneConfigComponent implements OnInit {
  constructor(
    private apiCallService: ApiCallService,
    private _snackBar: SnackbarCollection,
    private _i18nDynamicTranslate: TranslateDynamicText,
    private global: Globals
  ) {}
  @ViewChild("milestoneConfig")
  milestoneConfig: ChallengesMilestoneConfigSetUpComponent;
  @Output() milestoneConfigFunction = new EventEmitter<any>();
  @Input() isEdit: boolean;
  @Input() configData: any;
  @Input() status;

  dataForm = new FormGroup({
    name: new FormControl("", [
      Validators.required,
      Validators.pattern(/^[\p{L}\p{N}\s.,'"<>\-()!?/]+$/u),
      Validators.minLength(3),
      maxLengthHtml(50),
    ]),
    aliasName: new FormControl("", [
      Validators.pattern(/^(\s+\S+\s*)*(?!\s).*$/),
      Validators.minLength(3),
      maxLengthHtml(50),
    ]),
    header: new FormControl("", [
      maxLengthHtml(300),
      Validators.pattern(/^[\p{L}\p{N}\s.,'"<>\-()!?/]+$/u),
    ]),
    description: new FormControl("", [
      maxLengthHtml(300),
      Validators.pattern(/^[\p{L}\p{N}\s.,'"<>\-()!?/]+$/u),
    ]),
    aliasDescription: new FormControl("", [maxLengthHtml(300)]),
  });
  duplicateNameErrorText: String = "";
  badgeImageUrl = "";
  uploading1 = false;
  submitClicked = false;
  preSelectedLogicalExpressions = null;
  milestoneConfigData: any = [];
  nameEditor: Editor;
  aliasNameEditor: Editor;
  headerEditor: Editor;
  descriptionEditor: Editor;
  aliasDescriptionEditor: Editor;
  html: "";
  toolbar: Toolbar = [["bold"]];

  ngOnInit() {
    const blockImagePastePlugin = new Plugin({
      key: new PluginKey("blockImagePastePlugin"),
      props: {
        handlePaste(view, event) {
          const clipboardData = (event as ClipboardEvent).clipboardData;
          if (!clipboardData) return false;

          for (let i = 0; i < clipboardData.items.length; i++) {
            if (clipboardData.items[i].type.startsWith("image/")) {
              // Block image pasting
              event.preventDefault();
              return true; // Prevent paste from continuing
            }
          }

          return false; // allow non-image paste
        },
      },
    });

    const plainTextPastePlugin = new Plugin({
      key: new PluginKey("plainTextPastePlugin"),
      props: {
        handlePaste(view, event, _slice) {
          const clipboardData = (event as ClipboardEvent).clipboardData;
          if (!clipboardData) return false;

          const text = clipboardData.getData("text/plain");
          if (!text) return false;

          event.preventDefault();

          const { schema } = view.state;

          // Insert plain text as paragraph nodes
          const nodes = text
            .split(/\n{2,}/) // split by double newlines = paragraph
            .map((paragraphText) =>
              schema.nodes.paragraph.create(
                null,
                schema.text(paragraphText.trim())
              )
            );

          const slice = new Slice(Fragment.fromArray(nodes), 1, 1);
          const transaction = view.state.tr.replaceSelection(slice);
          view.dispatch(transaction);

          return true;
        },
      },
    });

    this.aliasNameEditor = new Editor({
      // Add your schema, history, etc. if needed
      content: "",
      plugins: [blockImagePastePlugin, plainTextPastePlugin],
    });
    this.nameEditor = new Editor({
      // Add your schema, history, etc. if needed
      content: "",
      plugins: [blockImagePastePlugin, plainTextPastePlugin],
    });
    this.headerEditor = new Editor({
      // Add your schema, history, etc. if needed
      content: "",
      plugins: [blockImagePastePlugin, plainTextPastePlugin],
    });
    this.aliasDescriptionEditor = new Editor({
      // Add your schema, history, etc. if needed
      content: "",
      plugins: [blockImagePastePlugin, plainTextPastePlugin],
    });
    this.descriptionEditor = new Editor({
      // Add your schema, history, etc. if needed
      content: "",
      plugins: [blockImagePastePlugin, plainTextPastePlugin],
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log(this.configData, this.isEdit);
    if (changes.configData) {
      this.updateSelectedData();
    }
  }

  ngAfterViewInit(): void {
    console.log(this.configData, this.isEdit);
    this.updateSelectedData();
  }

  updateSelectedData() {
    if (this.isEdit && this.configData) {
      const {
        name,
        aliasName,
        description,
        aliasDescription,
        heading,
        image,
        milestoneconfig,
      } = this.configData;

      // Set values only if they're defined
      if (name != null)
        this.dataForm.get("name").setValue(this.convertMarkdownToHtml(name));
      if (aliasName != null)
        this.dataForm
          .get("aliasName")
          .setValue(this.convertMarkdownToHtml(aliasName));
      if (description != null)
        this.dataForm
          .get("description")
          .setValue(this.convertMarkdownToHtml(description));
      if (aliasDescription != null)
        this.dataForm
          .get("aliasDescription")
          .setValue(this.convertMarkdownToHtml(aliasDescription));
      if (heading != null)
        this.dataForm
          .get("header")
          .setValue(this.convertMarkdownToHtml(heading));
      if (image != null) this.badgeImageUrl = image;
      if (milestoneconfig?.barrier != null) {
        this.milestoneConfig.fetchedLogicalExpressions =
          milestoneconfig.barrier;
      }
    }
  }

  stringInputHandler(event: KeyboardEvent, maxLength: number, type: string) {
    if (this.duplicateNameErrorText) this.duplicateNameErrorText = "";

    // Allow Ctrl/Command + A, C, X, V, and Arrow keys
    if (
      (event.ctrlKey || event.metaKey) &&
      ["a", "c", "x", "v"].includes(event.key.toLowerCase())
    ) {
      return; // Allow these actions
    }

    // Get the value inside ngx-editor
    const control = this.dataForm.get(type);
    if (!control) return;

    const inputValue = control.value || "";

    if (inputValue?.replace(/<\/?[^>]+(>|$)/g, "").length > 1) {
      const clearError = (id: string) => {
        const el = document.getElementById(id);
        if (el) el.innerHTML = "";
      };

      // Clear previous errors
      if (type == "name") {
        clearError("name-error");
      }
      if (type == "aliasName") {
        clearError("aliasName-error");
      }
    }

    // Prevent input if max length is reached (except for Backspace)
    if (
      inputValue?.replace(/<\/?[^>]+(>|$)/g, "").length >= maxLength &&
      event.key !== "Backspace"
    ) {
      event.preventDefault();
      return false;
    }
  }

  convertBoldToMarkdown(html: string): string {
    return this.global.convertBoldToMarkdown(html);
  }

  convertMarkdownToHtml(markdown: string): string {
    return this.global.convertMarkdownToHtml(markdown);
  }

  getCountOfText(value: string) {
    return value?.replace(/<\/?[^>]+(>|$)/g, "").length || 0;
  }

  setUploadedFile(event, imageType) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      console.warn("No file selected.");
      return;
    }
    if (imageType == "MILESTONE") {
      this.uploading1 = true;
    }
    const fd = new FormData();
    fd.append("files", event.target.files[0], event.target.files[0].name);
    this.apiCallService.uploadChallengeImage(fd, imageType).subscribe(
      (response) => {
        // console.log('Response received:', response);
        if (imageType === "MILESTONE") {
          this.uploading1 = false;
          this.badgeImageUrl = response["body"][0] + "?v=" + Date.now();
        }
      },
      (error) => {
        this._snackBar.openSnackBar("Error uploading image", 2000);
        this.uploading1 = false;
      }
    );
  }

  selectChallengeType(value) {
    if (value === "goback") {
      let data = {
        isGoBack: value === "goback" ? true : false,
      };
      this.milestoneConfigFunction.emit(data);
      return;
    }
    let checkStatus = this.validateData();
    if (!this.milestoneConfig.isLogicalExpressionsInvalid() && checkStatus) {
      this.milestoneConfigData =
        this.milestoneConfig.prepareSegmentDefinition();
      let data = {
        nextView: "reward",
        isExit: value === "exit" ? true : false,
        isGoBack: value === "goback" ? true : false,
        configData: {
          name: this.convertBoldToMarkdown(this.dataForm.get("name").value),
          aliasName: this.convertBoldToMarkdown(
            this.dataForm.get("aliasName").value
          ),
          description: this.convertBoldToMarkdown(
            this.dataForm.get("description").value
          ),
          aliasDescription: this.convertBoldToMarkdown(
            this.dataForm.get("aliasDescription").value
          ),
          heading: this.convertBoldToMarkdown(
            this.dataForm.get("header").value
          ),
          image: this.badgeImageUrl,
          milestoneconfig: this.milestoneConfigData,
          milestoneconfigApi:
            this.milestoneConfig.prepareSegmentDefinitionForNode(),
        },
      };
      this.milestoneConfigFunction.emit(data);
    }
  }

  stripHtmlTags(value: string): string {
    const doc = new DOMParser().parseFromString(value || "", "text/html");
    return doc.body.textContent || "";
  }

  validateData(): boolean {
    const englishOnlyRegex =
      /^(?!\s)[A-Za-z0-9\s.,'"<>\-()!?/_|]*$/;
    this.submitClicked = true;
    this.dataForm.markAllAsTouched();
    this.dataForm.updateValueAndValidity();

    // Clean HTML from values without updating form controls
    const cleanedValues: Record<string, string> = {};
    const fieldsToClean = [
      "name",
      "aliasName",
      "description",
      "aliasDescription",
      "header",
    ];
    fieldsToClean.forEach((field) => {
      const control = this.dataForm.get(field);
      const rawValue = control?.value || "";
      cleanedValues[field] = this.stripHtmlTags(rawValue).trim();
    });

    const name = cleanedValues["name"];
    const aliasName = cleanedValues["aliasName"];
    const description = cleanedValues["description"];
    const aliasDescription = cleanedValues["aliasDescription"];
    const header = cleanedValues["header"];

    const clearError = (id: string) => {
      const el = document.getElementById(id);
      if (el) el.innerHTML = "";
    };

    // Clear previous errors
    clearError("name-error");
    clearError("aliasName-error");
    clearError("description-error");
    clearError("aliasDescription-error");
    clearError("header-error");

    let isValid = true;

    // Validate name
    if (
      !name ||
      name.length === 0 ||
      name.length < 3 ||
      name.length > 50 ||
      !englishOnlyRegex.test(name)
    ) {
      let errorMsg = "";

      if (name.length > 50) {
        errorMsg = "Milestone name should not exceed 50 characters";
      } else if (name.length === 0) {
        errorMsg = "Milestone name should not be empty";
      } else if (name.length < 3) {
        errorMsg = "Milestone name should have at least 3 characters";
      } else {
        errorMsg =
          "Milestone name must contain only English characters, numbers, spaces, punctuation and underscores, and cannot start with a space.";
      }

      document.getElementById("name-error")!.innerHTML =
        this._i18nDynamicTranslate.transform(errorMsg, ["SHARED_PAGE"]);

      isValid = false;
    }

    // Validate aliasName
    if (
      aliasName.length > 50 ||
      (aliasName.length < 3 && aliasName.length > 1)
    ) {
      const errorMsg =
        aliasName.length > 50
          ? "Alias Milestone name should not exceed 50 characters"
          : "Alias Milestone name should have at least 3 characters";
      document.getElementById("aliasName-error")!.innerHTML =
        this._i18nDynamicTranslate.transform(errorMsg, ["SHARED_PAGE"]);
      isValid = false;
    }

    // Validate description
    if (
      description.length > 300 ||
      (description.length > 0 && !englishOnlyRegex.test(description))
    ) {
      let errorMsg = "";

      if (description.length > 300) {
        errorMsg = "Milestone description should not exceed 300 characters";
      } else {
        errorMsg =
          "Milestone description must contain only English characters, numbers, spaces, punctuation and underscores.";
      }

      document.getElementById("description-error")!.innerHTML =
        this._i18nDynamicTranslate.transform(errorMsg, ["SHARED_PAGE"]);

      isValid = false;
    }

    // Validate aliasDescription
    if (aliasDescription.length > 300) {
      document.getElementById("aliasDescription-error")!.innerHTML =
        this._i18nDynamicTranslate.transform(
          "Alias Milestone description should not exceed 300 characters",
          ["SHARED_PAGE"]
        );
      isValid = false;
    }

    // Validate header
    if (
      header.length > 300 ||
      (header.length > 0 && !englishOnlyRegex.test(header))
    ) {
      let errorMsg = "";

      if (header.length > 300) {
        errorMsg = "Milestone header should not exceed 300 characters";
      } else {
        errorMsg =
          "Milestone header must contain only English characters, numbers, spaces, punctuation and underscores.";
      }

      document.getElementById("header-error")!.innerHTML =
        this._i18nDynamicTranslate.transform(errorMsg, ["SHARED_PAGE"]);

      isValid = false;
    }

    return isValid;
  }
}
